<template>
  <div class="p-0 overflow-hidden">
    <Carousel
      ref="myCarousel"
      class="w-100 p-0 h-100 overflow-hidden"
      v-model="slideIndex"
    >
      <template #addons="{ currentSlide }">
        <section class="position-absolute top-0 w-100 d-flex flex-row">
          <div id="dots" class="mt-5 position-relative">
            <span
              v-for="index in slides"
              :class="[
                index - 1 === currentSlide ? 'active-dot' : 'dot',
                index - 1 < currentSlide ? 'dot-activated' : '',
              ]"
              :key="index"
              @click="switchSlide(index)"
              @pointerdown="switchSlide(index)"
              :style="{
                background: setAddonsColors(currentSlide, index - 1),
              }"
            >
            </span>
          </div>
          <button
            class="dismiss-alert-button position-relative"
            data-cy="alert-dismiss-button"
            @click="$emit('dismiss')"
          >
            <i
              class="fa fa-times"
              :style="{
                color: accentDotsIndex?.includes(currentSlide)
                  ? dotsAccentColor
                  : 'white',
              }"
            ></i>
          </button>
        </section>
      </template>
      <slot class="carousel__item position-absolute"></slot>
    </Carousel>
    <div class="loading-overlay" v-if="fetching">
      <i class="fa fa-spin fa-spinner" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { Carousel } from "vue3-carousel";

const props = defineProps({
  slides: {
    type: Number,
    default: 1,
  },
  dotsBaseColor: {
    type: String,
    default: "white",
  },
  dotActiveColor: {
    type: String,
    default: "white",
  },
  dotsAccentColor: {
    type: String,
    default: "#11111138",
  },
  accentDotsIndex: {
    type: Array as () => number[] | undefined,
  },
});

defineEmits<{
  (e: "update:slideIndex", value: number): void;
  (e: "dismiss"): void;
}>();

const myCarousel = ref();

const slideIndex = ref<number>(0);
const fetching = ref<boolean>(false);

function switchSlide(newIndex: number) {
  console.log({ newIndex });
  if (newIndex <= props.slides) {
    myCarousel.value?.slideTo(newIndex - 1);
    slideIndex.value = newIndex - 1;
  }
}

function nextSlide(): boolean {
  if (slideIndex.value === props.slides) {
    return true;
  } else {
    myCarousel.value?.next();
    return false;
  }
}

function setAddonsColors(currentSlide: number, index: number): string {
  if (currentSlide === index) {
    if (props.accentDotsIndex?.includes(currentSlide)) {
      return props.dotsAccentColor;
    }
    return props.dotActiveColor;
  }
  return props.dotsBaseColor;
}

onMounted(() => {
  try {
    document.querySelectorAll(".carousel__slide")?.forEach((el) => {
      (el as HTMLElement).style.height = window.outerHeight + "px";
    });

    document.querySelectorAll(".carousel__viewport")?.forEach((el) => {
      (el as HTMLElement).style.maxHeight = window.outerHeight + "px";
    });
  } catch (error) {
    console.log(error);
  }
});

defineExpose({ nextSlide });
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

#application-btn {
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  border: none;
  bottom: 3%;
  text-align: center;
  width: 90%;
  border-radius: 10px !important;
  background: #336417;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.32);
}

#slide-text {
  margin-left: auto;
  margin-right: auto;
  padding-left: 10%;
  padding-right: 10%;
  left: 0;
  right: 0;
  top: 10%;
  text-align: center;
  font-size: x-large;
}

#slide-text span {
  font-weight: bolder;
  color: black;
}

#dots {
  margin-left: auto;
  margin-right: 0;
  top: 18%;
  width: 85%;
  left: 0;
  text-align: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.dot {
  height: 10px;
  width: 10px;
  border-radius: 100px;
  background: rgba(17, 17, 17, 0.16);
  display: inline-block;
  margin-left: 2.5%;
}

.dot-activated {
  height: 10px;
  width: 10px;
  border-radius: 100px;
  background-color: #2c7040;
  filter: contrast(1);
  display: inline-block;
  margin-left: 2.5%;
}

.active-dot {
  height: 10px;
  width: 20px;
  border-radius: 100px;
  display: inline-block;
  margin-left: 2.5%;
  transition: width 0.3s ease-out;
}

#opt-out-btn {
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  bottom: 12%;
  height: 56px;
  padding: 10px 24px;
  font-size: medium;
  color: #336417;
  font-weight: 800;
  font-style: normal;
  line-height: 20px;
  letter-spacing: -0.2px;
  width: 90%;
  border-radius: 10px;
  border: none;
  background: #fff;
  box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.32);
  animation: bounce-in-right 0.5s ease;
}

@keyframes bounce-in-right {
  0% {
    opacity: 0;
    transform: translateY(2000px);
  }
  40% {
    opacity: 1;
    transform: translateY(-30px);
  }
  80% {
    transform: translateY(10px);
  }
  100% {
    transform: translateY(0);
  }
}

.dismiss-alert-button {
  background: none;
  border: none;
  padding: 0;
  color: #606060;
  right: 3.5%;
  margin-left: 0;
  margin-top: 7%;
  scale: 2;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-green;
  font-size: 40px;
  background-color: rgba(150, 150, 150, 0.4);
  width: 100vw;
  height: 100vh;
  z-index: 1000;
}
</style>
